import { Fragment } from "prosemirror-model";
import type { NodeSpec } from "prosemirror-model";

import { booleanAttr, domAttrs, numberAttr, stringAttr } from "./attrs.js";

/**
 * Node names, attribute names, attribute order and content expressions follow
 * `docs/spec/markdown-dialect.md` §4 exactly: the `data` JSON is part of the
 * compatibility surface, so the vocabulary is not ours to choose.
 */
export const NodeName = {
  doc: "doc",
  paragraph: "paragraph",
  heading: "heading",
  blockquote: "blockquote",
  bulletList: "bullet_list",
  orderedList: "ordered_list",
  listItem: "list_item",
  checkboxList: "checkbox_list",
  checkboxItem: "checkbox_item",
  codeBlock: "code_block",
  codeFence: "code_fence",
  hr: "hr",
  table: "table",
  tableRow: "tr",
  tableCell: "td",
  tableHeader: "th",
  image: "image",
  video: "video",
  attachment: "attachment",
  embed: "embed",
  containerNotice: "container_notice",
  containerToggle: "container_toggle",
  mathInline: "math_inline",
  mathBlock: "math_block",
  emoji: "emoji",
  mention: "mention",
  br: "br",
  text: "text",
} as const;

export type NodeNameValue = (typeof NodeName)[keyof typeof NodeName];

export const NOTICE_STYLES = ["info", "success", "warning", "tip"] as const;
export type NoticeStyle = (typeof NOTICE_STYLES)[number];

export const ORDERED_LIST_STYLES = ["number", "lower-alpha", "upper-alpha"] as const;
export type OrderedListStyle = (typeof ORDERED_LIST_STYLES)[number];

export const RULE_MARKUPS = ["---", "___", "***"] as const;
export type RuleMarkup = (typeof RULE_MARKUPS)[number];

/** A rule whose markup is `***` renders as a page break. */
export const PAGE_BREAK_MARKUP = "***";

export const IMAGE_LAYOUT_CLASSES = ["right-50", "left-50", "full-width"] as const;
export type ImageLayoutClass = (typeof IMAGE_LAYOUT_CLASSES)[number];

export const MENTION_TYPES = [
  "user",
  "document",
  "collection",
  "group",
  "issue",
  "pull_request",
  "project",
  "url",
  "date",
] as const;
export type MentionType = (typeof MENTION_TYPES)[number];

export const CELL_ALIGNMENTS = ["left", "center", "right"] as const;
export type CellAlignment = (typeof CELL_ALIGNMENTS)[number];

/** What a notice may hold; anything else makes the whole notice unplaceable. */
const NOTICE_CONTENT =
  "(list | blockquote | hr | paragraph | heading | code_block | code_fence | attachment)+";

function alignmentFromStyle(value: string | null): CellAlignment | null {
  if (!value) return null;
  const match = /text-align\s*:\s*(left|center|right)/i.exec(value);
  const alignment = match?.[1]?.toLowerCase();
  if (alignment === "left" || alignment === "center" || alignment === "right") return alignment;
  return null;
}

function cellAttrs(dom: HTMLElement): Record<string, unknown> {
  const colspan = dom.getAttribute("colspan");
  const rowspan = dom.getAttribute("rowspan");
  const colwidth = dom.getAttribute("data-colwidth");
  const background = dom.style.backgroundColor;
  return {
    colspan: colspan ? Number(colspan) : 1,
    rowspan: rowspan ? Number(rowspan) : 1,
    alignment: alignmentFromStyle(dom.getAttribute("style")) ?? dom.getAttribute("align"),
    colwidth: colwidth ? Number(colwidth) : null,
    // The cell background colour rides along in `marks`, as §4 describes.
    marks: background ? [{ type: "background", color: background }] : undefined,
  };
}

const cellSpec = (tag: "td" | "th"): NodeSpec => ({
  content: "block+",
  // Attribute declaration order is the JSON key order; §4 fixes it.
  attrs: {
    colspan: { default: 1 },
    rowspan: { default: 1 },
    alignment: { default: null },
    colwidth: { default: null },
    marks: { default: undefined },
  },
  group: "cell",
  tableRole: tag === "td" ? "cell" : "header_cell",
  isolating: true,
  parseDOM: [{ tag, getAttrs: cellAttrs }],
  toDOM: (node) => {
    const alignment = stringAttr(node, "alignment");
    const colspan = numberAttr(node, "colspan") ?? 1;
    const rowspan = numberAttr(node, "rowspan") ?? 1;
    const colwidth = numberAttr(node, "colwidth");
    return [
      tag,
      domAttrs({
        colspan: colspan === 1 ? null : colspan,
        rowspan: rowspan === 1 ? null : rowspan,
        "data-colwidth": colwidth,
        style: alignment === null ? null : `text-align: ${alignment}`,
      }),
      0,
    ];
  },
});

const codeBlockSpec = (): NodeSpec => ({
  attrs: { language: { default: "javascript" }, wrap: { default: false } },
  content: "text*",
  // A code block carries no formatting, but a comment may anchor inside it.
  marks: "comment",
  group: "block",
  code: true,
  defining: true,
  parseDOM: [
    {
      tag: "pre",
      preserveWhitespace: "full",
      getAttrs: (dom) => {
        const code = dom.querySelector("code");
        const className = code?.getAttribute("class") ?? dom.getAttribute("class") ?? "";
        const match = /language-([\w+-]+)/.exec(className);
        return {
          language: match?.[1] ?? dom.getAttribute("data-language") ?? "",
          wrap: dom.getAttribute("data-wrap") === "true",
        };
      },
    },
  ],
  toDOM: (node) => {
    const language = stringAttr(node, "language");
    return [
      "pre",
      domAttrs({
        "data-language": language,
        "data-wrap": booleanAttr(node, "wrap") ? "true" : null,
      }),
      ["code", domAttrs({ class: language ? `language-${language}` : null }), 0],
    ];
  },
});

export const nodes: Record<NodeNameValue, NodeSpec> = {
  doc: { content: "block+" },

  paragraph: {
    content: "inline*",
    group: "block",
    parseDOM: [{ tag: "p" }],
    toDOM: () => ["p", 0],
  },

  text: { group: "inline" },

  heading: {
    // `collapsed` defaults to undefined so it stays out of `data` entirely
    // until the editor folds the heading.
    attrs: { level: { default: 1 }, collapsed: { default: undefined } },
    content: "inline*",
    group: "block",
    defining: true,
    parseDOM: [1, 2, 3, 4, 5, 6].map((level) => ({
      tag: `h${String(level)}`,
      getAttrs: (dom: HTMLElement) => ({
        level,
        collapsed: dom.getAttribute("data-collapsed") === "true" ? true : undefined,
      }),
    })),
    toDOM: (node) => {
      const level = numberAttr(node, "level") ?? 1;
      return [
        `h${String(Math.min(6, Math.max(1, level)))}`,
        domAttrs({ "data-collapsed": booleanAttr(node, "collapsed") ? "true" : null }),
        0,
      ];
    },
  },

  blockquote: {
    content: "block+",
    group: "block",
    defining: true,
    parseDOM: [{ tag: "blockquote" }],
    toDOM: () => ["blockquote", 0],
  },

  bullet_list: {
    content: "list_item+",
    group: "block list",
    parseDOM: [{ tag: "ul" }],
    toDOM: () => ["ul", 0],
  },

  ordered_list: {
    attrs: { order: { default: 1 }, listStyle: { default: "number" } },
    content: "list_item+",
    group: "block list",
    parseDOM: [
      {
        tag: "ol",
        getAttrs: (dom) => {
          const start = dom.getAttribute("start");
          const type = dom.getAttribute("type");
          const listStyle: OrderedListStyle =
            type === "a" ? "lower-alpha" : type === "A" ? "upper-alpha" : "number";
          return { order: start ? Number(start) : 1, listStyle };
        },
      },
    ],
    toDOM: (node) => {
      const order = numberAttr(node, "order") ?? 1;
      const listStyle = stringAttr(node, "listStyle");
      return [
        "ol",
        domAttrs({
          start: order === 1 ? null : order,
          type: listStyle === "lower-alpha" ? "a" : listStyle === "upper-alpha" ? "A" : null,
        }),
        0,
      ];
    },
  },

  list_item: {
    content: "block+",
    defining: true,
    draggable: true,
    parseDOM: [{ tag: "li" }],
    toDOM: () => ["li", 0],
  },

  checkbox_list: {
    attrs: { id: { default: null } },
    content: "checkbox_item+",
    group: "block list",
    parseDOM: [
      { tag: "ul[data-checkbox-list]", priority: 60 },
      {
        // A list whose items carry checkbox inputs is a task list, whichever
        // editor produced the clipboard HTML.
        tag: "ul",
        priority: 60,
        getAttrs: (dom) => (dom.querySelector("input[type=checkbox]") ? null : false),
      },
    ],
    toDOM: () => ["ul", { "data-checkbox-list": "true" }, 0],
  },

  checkbox_item: {
    attrs: { checked: { default: false } },
    content: "block+",
    defining: true,
    draggable: true,
    parseDOM: [
      {
        tag: "li[data-checked]",
        priority: 60,
        getAttrs: (dom) => ({ checked: dom.getAttribute("data-checked") === "true" }),
      },
      {
        // Clipboard HTML from other editors puts a checkbox input inside the li.
        tag: "li",
        priority: 60,
        getAttrs: (dom) => {
          const input = dom.querySelector("input[type=checkbox]");
          if (!input) return false;
          return { checked: input.hasAttribute("checked") };
        },
      },
    ],
    toDOM: (node) => ["li", { "data-checked": booleanAttr(node, "checked") ? "true" : "false" }, 0],
  },

  code_block: codeBlockSpec(),
  // A schema alias accepted in `data`; the parser never produces it.
  code_fence: codeBlockSpec(),

  hr: {
    attrs: { markup: { default: "---" } },
    group: "block",
    parseDOM: [
      { tag: "hr", getAttrs: (dom) => ({ markup: dom.getAttribute("data-markup") ?? "---" }) },
      { tag: "div[data-page-break]", getAttrs: () => ({ markup: PAGE_BREAK_MARKUP }) },
    ],
    toDOM: (node) => {
      const markup = stringAttr(node, "markup") ?? "---";
      return [
        "hr",
        domAttrs({
          "data-markup": markup,
          "data-page-break": markup === PAGE_BREAK_MARKUP ? "true" : null,
        }),
      ];
    },
  },

  table: {
    attrs: { layout: { default: null } },
    content: "tr+",
    group: "block",
    tableRole: "table",
    isolating: true,
    parseDOM: [{ tag: "table" }],
    toDOM: () => ["table", ["tbody", 0]],
  },

  tr: {
    content: "(th | td)*",
    tableRole: "row",
    parseDOM: [{ tag: "tr" }],
    toDOM: () => ["tr", 0],
  },

  td: cellSpec("td"),
  th: cellSpec("th"),

  image: {
    // `width`/`height` default to undefined, so a size-less image carries no
    // size keys at all and ` =640x` carries only a width.
    attrs: {
      src: { default: "" },
      width: { default: undefined },
      height: { default: undefined },
      alt: { default: null },
      source: { default: null },
      layoutClass: { default: null },
      title: { default: null },
      marks: { default: undefined },
    },
    content: "text*",
    inline: true,
    group: "inline",
    atom: true,
    draggable: true,
    selectable: true,
    parseDOM: [
      {
        tag: "figure",
        // The node is an atom; without this the parser would also descend into
        // the inner <img> and the caption.
        getContent: () => Fragment.empty,
        getAttrs: (dom) => {
          const img = dom.querySelector("img");
          if (!img) return false;
          const caption = dom.querySelector("figcaption");
          const width = img.getAttribute("width");
          const height = img.getAttribute("height");
          return {
            src: img.getAttribute("src") ?? "",
            width: width ? Number(width) : undefined,
            height: height ? Number(height) : undefined,
            alt: caption?.textContent ?? img.getAttribute("alt"),
            source: dom.getAttribute("data-source"),
            layoutClass: dom.getAttribute("class"),
            title: img.getAttribute("title"),
          };
        },
      },
      {
        tag: "img[src]",
        getAttrs: (dom) => {
          const width = dom.getAttribute("width") ?? dom.style.width.replace(/px$/, "");
          const height = dom.getAttribute("height") ?? dom.style.height.replace(/px$/, "");
          return {
            src: dom.getAttribute("src") ?? "",
            width: width ? Number(width) : undefined,
            height: height ? Number(height) : undefined,
            alt: dom.getAttribute("alt"),
            source: dom.getAttribute("data-source"),
            layoutClass: dom.getAttribute("class"),
            title: dom.getAttribute("title"),
          };
        },
      },
    ],
    toDOM: (node) => [
      "figure",
      domAttrs({
        class: stringAttr(node, "layoutClass"),
        "data-source": stringAttr(node, "source"),
      }),
      [
        "img",
        domAttrs({
          src: stringAttr(node, "src") ?? "",
          alt: stringAttr(node, "alt"),
          title: stringAttr(node, "title"),
          width: numberAttr(node, "width"),
          height: numberAttr(node, "height"),
        }),
      ],
      ["figcaption", stringAttr(node, "alt") ?? ""],
    ],
  },

  video: {
    attrs: {
      id: { default: null },
      src: { default: null },
      width: { default: null },
      height: { default: null },
      title: { default: null },
    },
    group: "block",
    atom: true,
    draggable: true,
    parseDOM: [
      {
        tag: "video[src]",
        getAttrs: (dom) => ({
          id: dom.getAttribute("data-id"),
          src: dom.getAttribute("src") ?? "",
          width: dom.getAttribute("width") ? Number(dom.getAttribute("width")) : null,
          height: dom.getAttribute("height") ? Number(dom.getAttribute("height")) : null,
          title: dom.getAttribute("title"),
        }),
      },
    ],
    toDOM: (node) => [
      "video",
      domAttrs({
        src: stringAttr(node, "src") ?? "",
        title: stringAttr(node, "title"),
        width: numberAttr(node, "width"),
        height: numberAttr(node, "height"),
        controls: "true",
      }),
    ],
  },

  attachment: {
    // `size` is a string: an attachment can exceed the safe integer range.
    attrs: {
      id: { default: null },
      href: { default: null },
      title: { default: "" },
      size: { default: "0" },
      preview: { default: false },
      width: { default: null },
      height: { default: null },
      contentType: { default: null },
    },
    group: "block",
    atom: true,
    draggable: true,
    parseDOM: [
      {
        tag: "a[data-attachment]",
        priority: 60,
        getAttrs: (dom) => ({
          id: dom.getAttribute("data-id"),
          href: dom.getAttribute("href") ?? "",
          title: dom.getAttribute("title") ?? dom.textContent,
          size: dom.getAttribute("data-size") ?? "0",
          contentType: dom.getAttribute("type"),
        }),
      },
    ],
    toDOM: (node) => [
      "a",
      domAttrs({
        "data-attachment": "true",
        href: stringAttr(node, "href") ?? "",
        title: stringAttr(node, "title"),
        "data-size": stringAttr(node, "size") ?? String(numberAttr(node, "size") ?? 0),
      }),
      stringAttr(node, "title") ?? "",
    ],
  },

  embed: {
    attrs: { href: { default: "" }, width: { default: null }, height: { default: null } },
    content: "inline*",
    group: "block",
    atom: true,
    parseDOM: [
      { tag: "iframe[src]", getAttrs: (dom) => ({ href: dom.getAttribute("src") ?? "" }) },
      {
        tag: "div[data-embed]",
        getAttrs: (dom) => ({ href: dom.getAttribute("data-embed") ?? "" }),
      },
    ],
    toDOM: (node) => [
      "div",
      domAttrs({ "data-embed": stringAttr(node, "href") ?? "" }),
      ["iframe", domAttrs({ src: stringAttr(node, "href") ?? "" })],
    ],
  },

  container_notice: {
    attrs: { style: { default: "info" } },
    content: NOTICE_CONTENT,
    group: "block",
    defining: true,
    parseDOM: [
      {
        tag: "div[data-notice]",
        getAttrs: (dom) => ({ style: dom.getAttribute("data-notice") ?? "info" }),
      },
      {
        // Confluence-style info macros arrive on the clipboard as classed divs.
        tag: "div.confluence-information-macro",
        getAttrs: (dom) => {
          const className = dom.getAttribute("class") ?? "";
          const style = /warning|problem/i.test(className)
            ? "warning"
            : /tip|success/i.test(className)
              ? "tip"
              : "info";
          return { style };
        },
      },
    ],
    toDOM: (node) => ["div", domAttrs({ "data-notice": stringAttr(node, "style") ?? "info" }), 0],
  },

  container_toggle: {
    attrs: { id: { default: undefined } },
    // The first block is the toggle's title; the rest is its body.
    content: "(paragraph | heading) block*",
    group: "block",
    defining: true,
    parseDOM: [{ tag: "details" }],
    toDOM: () => ["details", 0],
  },

  math_block: {
    content: "text*",
    marks: "",
    group: "block",
    code: true,
    atom: true,
    parseDOM: [{ tag: "div[data-math-block]", preserveWhitespace: "full" }],
    toDOM: () => ["div", { "data-math-block": "true" }, 0],
  },

  math_inline: {
    content: "text*",
    marks: "",
    inline: true,
    group: "inline",
    code: true,
    atom: true,
    parseDOM: [{ tag: "span[data-math-inline]" }],
    toDOM: () => ["span", { "data-math-inline": "true" }, 0],
  },

  br: {
    inline: true,
    group: "inline",
    selectable: false,
    parseDOM: [{ tag: "br" }],
    toDOM: () => ["br"],
  },

  mention: {
    attrs: {
      type: { default: "user" },
      label: { default: "" },
      modelId: { default: "" },
      id: { default: "" },
      href: { default: undefined },
      anchorId: { default: undefined },
    },
    inline: true,
    group: "inline",
    atom: true,
    selectable: true,
    parseDOM: [
      {
        tag: "span[data-mention-type]",
        getAttrs: (dom) => ({
          type: dom.getAttribute("data-mention-type") ?? "user",
          label: dom.textContent ?? "",
          modelId: dom.getAttribute("data-model-id") ?? "",
          id: dom.getAttribute("data-mention-id") ?? "",
          href: dom.getAttribute("href") ?? undefined,
        }),
      },
    ],
    toDOM: (node) => [
      "span",
      domAttrs({
        "data-mention-type": stringAttr(node, "type") ?? "user",
        "data-mention-id": stringAttr(node, "id") ?? "",
        "data-model-id": stringAttr(node, "modelId") ?? "",
        href: stringAttr(node, "href"),
      }),
      stringAttr(node, "label") ?? "",
    ],
  },

  emoji: {
    attrs: { "data-name": { default: "grey_question" } },
    content: "text*",
    marks: "",
    inline: true,
    group: "inline",
    atom: true,
    parseDOM: [
      {
        tag: "span[data-emoji]",
        getAttrs: (dom) => ({ "data-name": dom.getAttribute("data-emoji") ?? "" }),
      },
    ],
    toDOM: (node) => [
      "span",
      domAttrs({ "data-emoji": stringAttr(node, "data-name") ?? "" }),
      stringAttr(node, "data-name") ?? "",
    ],
  },
};
